import React, { useState, useEffect, useContext, useRef } from 'react'
import ReactTooltip from 'react-tooltip'
import Toast from '@ingka/toast'
import { bool, func, string } from 'prop-types'
import { useTranslation } from 'react-i18next'
import classNames from 'classnames'
import Icon from 'components/uiElements/Icon'
import InputField from '@ingka/input-field'
import Tooltip from 'components/Tooltip'
import usePrevious from 'hooks/usePrevious'
import { compositionType } from 'models/propTypes'
import SugarcubeContext from 'apps/SugarcubeContext'
import SkipLinks from 'components/SkipLinks'
import { DESIGN, HEADER, NAVIGATION } from 'components/SkipLinks/constants'

import './PreviewHeader.scss'

const PreviewHeader = ({
    composition,
    isSaving,
    isModified,
    designingEnabled,
    onSave,
    onShareClick,
    editing,
    editName,
    rename,
    edit,
    checkEdit,
    openTryNewRoomModal,
    hasSaveError,
    hasRendered,
    openBag,
    hasAddedToBag,
    onCompositionInputBlur,
}) => {
    const [showSaveToast, setSaveSweepToast] = useState(false)
    const { t } = useTranslation()
    const prevIsSaving = usePrevious(isSaving)
    const { isMagicWindow, allowShare } = useContext(SugarcubeContext)

    // Remove MW check when design lab is done
    const canModify = composition && !isMagicWindow
    const canSave = !isSaving && (isModified || editing)
    const noRenderedItems = composition && composition.renderItems && composition.renderItems.length === 0
    const autoSelect = editName === t('design.default composition name')
    const toastText = hasSaveError ? t('design.your design could not be saved') : t('design.your design has been saved')

    const onSaveClick = () => {
        ReactTooltip.hide()
        onSave()
    }

    const inputRef = useRef(null)
    useEffect(() => {
        if (autoSelect && inputRef && inputRef.current) {
            inputRef.current.setSelectionRange(0, inputRef.current.value.length)
        }
    }, [])

    useEffect(() => {
        if (prevIsSaving && !isSaving) {
            setSaveSweepToast(true)
        }
    }, [prevIsSaving, isSaving])

    return (
        <div className={classNames('PreviewHeader', { hasRendered })}>
            <SkipLinks skipLinks={[DESIGN, HEADER]} skipContext={NAVIGATION} />
            <Toast
                text={toastText}
                isOpen={showSaveToast}
                onCloseRequest={() => {
                    setSaveSweepToast(false)
                }}
                onTransitionEnd={() => {}}
                ariaLabelCloseBtn={t('design.dismiss notification')}
            />
            {isMagicWindow && (
                <button onClick={openTryNewRoomModal} className="tryRoomButton">
                    <div className="tryRoom">
                        <Icon code="cube" svg />
                        <div>{t('design.explore more rooms')}</div>
                    </div>
                </button>
            )}
            <div className="compositionTitle">
                {canModify ? (
                    <div className={classNames('nameEditContainer', { designingEnabled })}>
                        <Tooltip id="design-name-input" />
                        <div className={classNames('nameEdit', { editing })} onClick={rename}>
                            <InputField
                                value={editName}
                                onChange={edit}
                                onKeyDown={checkEdit}
                                ref={inputRef}
                                autoFocus
                                data-testid="design-name-input"
                                onFocus={rename}
                                data-for="design-name-input"
                                data-tip={t('tooltip.rename this design')}
                                onBlur={onCompositionInputBlur}
                            />
                        </div>
                    </div>
                ) : null}
            </div>
            <nav role="navigation" className="previewCtaContainer">
                {canModify ? (
                    <>
                        <Tooltip id="save-design" />
                        <button
                            onClick={onSaveClick}
                            disabled={!(designingEnabled && canSave)}
                            data-tip={t('tooltip.save this design')}
                            data-for="save-design"
                            aria-label={t('tooltip.save this design')}
                        >
                            <Icon code="save" svg />
                        </button>
                        {allowShare && (
                            <>
                                <Tooltip id="share-design" />
                                <button
                                    onClick={() => onShareClick()}
                                    data-tip={t('tooltip.share this design')}
                                    disabled={!composition || noRenderedItems || !designingEnabled}
                                    data-for="share-design"
                                    aria-label={t('tooltip.share this design')}
                                >
                                    <Icon code="share" svg />
                                </button>
                            </>
                        )}
                    </>
                ) : null}
                <button onClick={openBag} aria-label={t('aria.shopping bag')}>
                    <Icon code={hasAddedToBag ? 'bag_active' : 'bag'} svg />
                </button>
            </nav>
        </div>
    )
}

PreviewHeader.defaultProps = {
    composition: null,
    onCompositionInputBlur: null,
}

PreviewHeader.propTypes = {
    composition: compositionType,
    isSaving: bool.isRequired,
    isModified: bool.isRequired,
    designingEnabled: bool.isRequired,
    onSave: func.isRequired,
    onShareClick: func.isRequired,
    rename: func.isRequired,
    edit: func.isRequired,
    checkEdit: func.isRequired,
    editing: bool.isRequired,
    editName: string.isRequired,
    openTryNewRoomModal: func.isRequired,
    hasSaveError: bool.isRequired,
    hasRendered: bool.isRequired,
    openBag: func.isRequired,
    hasAddedToBag: bool.isRequired,
    onCompositionInputBlur: func,
}
export default PreviewHeader
